import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listCompanies } from '../api';
import { money, words } from '../format';
import Logo from './Logo';

/* The universe, browsable. Search already reached all of this, but a search box
 * only answers a question you already have - there was no way to find out what
 * we cover without guessing a name, which made every company page feel like
 * something you had stumbled on rather than something we publish. */

const PAGE = 60;

export default function CompaniesHome() {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [failed, setFailed] = useState(false);

  // Kept apart from the empty list, which otherwise reports a failed request as
  // "nothing we cover answers to" an empty search box.
  useEffect(() => {
    listCompanies()
      .then((rows) => { setAll(rows); setFailed(false); })
      .catch(() => { setAll([]); setFailed(true); });
  }, []);

  /* Filtered here rather than on the server. The whole list arrives in one
   * request and is about 1,600 small rows, so matching in the browser is
   * instant where a round trip per keystroke would not be. */
  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!all) return [];
    if (!needle) return all;
    return all.filter((c) =>
      [c.name, c.ticker, c.sector, c.country, c.exchange]
        .some((f) => f && String(f).toLowerCase().includes(needle))
    );
  }, [all, q]);

  useEffect(() => setShown(PAGE), [q]);

  const loading = all === null;

  return (
    <div className="tw-page">
      <div className="tw-page-head">
        <div>
          <h1 className="tw-page-title">Companies</h1>
          <p className="tw-page-sub">
            Every company we cover, with how many of its terms we scan each week
            and how many are moving right now.
          </p>
        </div>
        {!loading && <span className="tw-page-count">{hits.length.toLocaleString()}</span>}
      </div>

      <input
        className="tw-filter"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name, ticker, sector or country"
        spellCheck={false}
        autoComplete="off"
      />

      {loading && (
        <div className="tw-block">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div className="tw-shimmer" key={i} style={{ height: 34, marginTop: 8 }} />
          ))}
        </div>
      )}

      {!loading && failed && (
        <div className="tw-empty">
          <div>
            <strong>We could not load the company list.</strong> This is a fault at
            our end. Reload, and tell us at xoxo@inflection.pro if it persists.
          </div>
        </div>
      )}

      {!loading && !failed && hits.length === 0 && (
        <div className="tw-empty">
          <div>
            <strong>No match.</strong> Nothing we cover answers to “{q.trim()}”.
          </div>
        </div>
      )}

      {!loading && hits.length > 0 && (
        <div className="tw-block">
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Listing</th>
                  <th>Sector</th>
                  <th className="n">Terms</th>
                  <th className="n">Open</th>
                  <th className="n">Market cap</th>
                </tr>
              </thead>
              <tbody>
                {hits.slice(0, shown).map((c) => (
                  <tr key={c.company_id}>
                    <td>
                      <Link className="tw-co-cell" to={`/companies/${c.company_id}`}>
                        <Logo symbol={c.eodhd_symbol} name={c.name} ticker={c.ticker} size={22} />
                        <span className="tw-co-name">{c.name}</span>
                        <span className="tw-ticker">{c.ticker}</span>
                      </Link>
                    </td>
                    <td>{[c.exchange, c.country].filter(Boolean).join(' · ')}</td>
                    <td>{words(c.sector)}</td>
                    <td className="n">{c.terms || '—'}</td>
                    <td className="n">
                      {c.open_trends
                        ? <span className="tw-open-pill">{c.open_trends}</span>
                        : <span style={{ color: 'var(--fg-dim)' }}>—</span>}
                    </td>
                    <td className="n">{money(c.market_cap_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {shown < hits.length && (
            <button type="button" className="tw-more" onClick={() => setShown((n) => n + PAGE)}>
              Show {Math.min(PAGE, hits.length - shown)} more
              <span> of {(hits.length - shown).toLocaleString()} left</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
